import { Clamp } from './Clamp.js';

export class Parallel {
  /**
   * Call an asynchronous function for each item in an array, allowing N instances of the function to be running at once, and return the results.
   * @template T - Type of the return value
   * @template E - Type of the array elements
   * @param {E[]} arr - array to loop over
   * @param {(item: E, index: number, inputArray: E[], stop:function) => T} func - function to call for each item in the array
   * @param {number} [numParallel] - max size to make each chunk (defaults to 1)
   * @returns {Promise<Awaited<T>[]>} Results of the function calls
   */
  static async map(arr, func, numParallel = 1) {
    // Create a queue by shallow copying the array.
    const queue = [...arr];
    // Initialize an index to keep track of the current item.
    let index = 0;
    // Create a results array.
    const results = Array(queue.length).fill(undefined);
    // Make sure numParallel is a number.
    if (typeof numParallel != 'number') throw new Error('`numParallel` must be a number');
    // Clamp numParallel between 1 and the length of the queue.
    numParallel = Clamp(numParallel, 1, queue.length);
    // Create an array to hold the promises.
    let stopProcessing = false;
    const promises = Array(numParallel)
      .fill(undefined)
      .map(async () => {
        while (index < queue.length) {
          // If stopProcessing is true, break out of the loop.
          if (stopProcessing) break;
          // Get the current index and increment it for the next loop.
          const _index = index++;
          // Get the item at the current index.
          const item = queue[_index];
          // Call the function and store the result in the results array at the current index.
          results[_index] = await func(item, _index, arr, () => (stopProcessing = true));
        }
      });
    // Wait for all promises to resolve.
    await Promise.all(promises);
    // Return the results.
    return results;
  }

  /**
   * Call an asynchronous function for each item in an array, allowing N instances of the function to be running at once.
   * @template E - Type of the array elements
   * @param {E[]} arr - array to loop over
   * @param {(item: E, index: number, inputArray: E[]) => void} func - function to call for each item in the array
   * @param {number} [numParallel] - max size to make each chunk (defaults to 1)
   * @returns {Promise<void>} Promise that resolves when all function calls are complete
   */
  static async forEach(arr, func, numParallel = 1) {
    // Call Parallel.map and discard the results.
    await Parallel.map(arr, func, numParallel);
  }
}
